import { useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  XAxis,
  YAxis,
} from "recharts";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { AppConfig } from "@/utils/AppConfig";
import { useTranslation } from "react-i18next";

type TotalSales = {
  key: string;
  sales: number;
  menuSales?: number | null;
  cashierSales?: number | null;
};

type TotalSalesChartProps = {
  data: TotalSales[];
};

export function TotalSalesChart({ data }: TotalSalesChartProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  // Determine which lines are available
  const hasMenuSales = useMemo(
    () => data.some((d) => typeof d.menuSales === "number"),
    [data]
  );
  const hasCashierSales = useMemo(
    () => data.some((d) => typeof d.cashierSales === "number"),
    [data]
  );

  const visibleKeys = {
    ...(hasMenuSales && { menuSales: true }),
    ...(hasCashierSales && { cashierSales: true }),
  };

  const [visibleLines, setVisibleLines] = useState(visibleKeys);

  const handleToggle = (key: keyof typeof visibleLines) => {
    const next = {
      ...visibleLines,
      [key]: !visibleLines[key],
    };
    const activeCount = Object.values(next).filter(Boolean).length;
    setVisibleLines(activeCount === 0 ? visibleKeys : next);
  };

  const chartConfig = {
    ...(hasMenuSales && {
      menuSales: {
        label: (
          <span
            dir={lang === "ar" ? "rtl" : "ltr"}
            className="flex items-center gap-1"
          >
            {t("TotalSalesChart.MenuSales")}{" "}
            {AppConfig.currency.icon("white", 10)}
          </span>
        ),
        color: "#4682B4",
      },
    }),
    ...(hasCashierSales && {
      cashierSales: {
        label: (
          <span
            dir={lang === "ar" ? "rtl" : "ltr"}
            className="flex items-center gap-1"
          >
            {t("TotalSalesChart.CashierSales")}{" "}
            {AppConfig.currency.icon("white", 10)}
          </span>
        ),
        color: "#FF6347",
      },
    }),
  } satisfies ChartConfig;

  // If no data lines are available, show nothing or fallback
  if (!hasMenuSales && !hasCashierSales) {
    return null; // Or a fallback UI like <p>No sales data available</p>
  }

  return (
    <Card className="force-light-mode border rounded-lg shadow-lg bg-white">
      <CardHeader>
        <CardTitle className="text-xl font-semibold text-gray-800">
          {t("TotalSalesChart.Title")}
        </CardTitle>
      </CardHeader>

      <CardContent>
        {/* Filter buttons */}
        <div className="flex justify-center mb-4">
          <div className="flex flex-col items-center gap-3">
            {hasMenuSales && (
              <button
                onClick={() => handleToggle("menuSales")}
                className="flex items-center gap-2 group"
              >
                <div
                  className={`w-4 h-4 rounded-sm border transition ${
                    visibleLines.menuSales
                      ? "bg-[#4682B4]"
                      : "bg-gray-200"
                  }`}
                />
                <span
                  className={`text-sm transition ${
                    visibleLines.menuSales
                      ? "text-gray-900 font-semibold"
                      : "text-gray-400"
                  }`}
                >
                  {t("TotalSalesChart.MenuSales")}
                </span>
              </button>
            )}
            {hasCashierSales && (
              <button
                onClick={() => handleToggle("cashierSales")}
                className="flex items-center gap-2 group"
              >
                <div
                  className={`w-4 h-4 rounded-sm border transition ${
                    visibleLines.cashierSales
                      ? "bg-[#FF6347]"
                      : "bg-gray-200"
                  }`}
                />
                <span
                  className={`text-sm transition ${
                    visibleLines.cashierSales
                      ? "text-gray-900 font-semibold"
                      : "text-gray-400"
                  }`}
                >
                  {t("TotalSalesChart.CashierSales")}
                </span>
              </button>
            )}
          </div>
        </div>

        <ChartContainer
          className="h-[500px] w-full bg-gray-100 rounded-lg py-6 shadow-inner"
          config={chartConfig}
        >
          <AreaChart data={data} margin={{ left: 12, right: 12 }}>
            <YAxis
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 12, fill: "#4A5568" }}
            />
            <CartesianGrid vertical={false} horizontal={false} />
            <XAxis
              dataKey="key"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tick={{ fontSize: 12, fill: "#4A5568" }}
              tickFormatter={(value) => value.slice(0, 4)}
            />
            <ChartTooltip
              cursor={{ stroke: "#CBD5E0", strokeWidth: 1 }}
              content={<ChartTooltipContent />}
            />
            <defs>
              {hasMenuSales && (
                <linearGradient
                  id="fillMenuSales"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor="#4682B4"
                    stopOpacity={0.8}
                  />
                  <stop
                    offset="95%"
                    stopColor="#4682B4"
                    stopOpacity={0.1}
                  />
                </linearGradient>
              )}
              {hasCashierSales && (
                <linearGradient
                  id="fillCashierSales"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor="#FF6347"
                    stopOpacity={0.8}
                  />
                  <stop
                    offset="95%"
                    stopColor="#FF6347"
                    stopOpacity={0.1}
                  />
                </linearGradient>
              )}
            </defs>

            {visibleLines.menuSales && (
              <Area
                dataKey="menuSales"
                type="monotone"
                fill="url(#fillMenuSales)"
                stroke="#4682B4"
                strokeWidth={2}
                fillOpacity={0.4}
              />
            )}
            {visibleLines.cashierSales && (
              <Area
                dataKey="cashierSales"
                type="monotone"
                fill="url(#fillCashierSales)"
                stroke="#FF6347"
                strokeWidth={2}
                fillOpacity={0.4}
              />
            )}
          </AreaChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
